import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import DoseItem from '~/components/DoseItem/DoseItem';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import useAvailableDoses from '~/hooks/dose/useAvailableDoses';
import { doseNameOf } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/DoseStep/doseUtils';
import useConfirmedSelection from '~/hooks/datasetSelection/useConfirmedSelection';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';

interface DoseListProps {
    onlyForImageUid?: string;
    hideHeading?: boolean;
}

export default function DoseList({
    onlyForImageUid,
    hideHeading,
}: DoseListProps = {}) {
    const intl = useIntl();

    const allDoses = useAvailableDoses(true);
    const availableDoses = allDoses.filter(
        (dose) =>
            onlyForImageUid === undefined || dose.imageUid === onlyForImageUid
    );
    const currentDose = useCurrentDose();
    const { pickDose } = useConfirmedSelection();
    const { deleteDose } = useDeleteDataset();

    const [deleteTarget, setDeleteTarget] = useState<DoseMatrix | null>(null);

    const handleConfirmDelete = () => {
        if (deleteTarget) {
            deleteDose(deleteTarget);
        }
        setDeleteTarget(null);
    };

    if (availableDoses.length === 0) {
        return null;
    }

    return (
        <>
            <Box>
                {!hideHeading && (
                    <Box px={2} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="doses" />
                        </Typography>
                    </Box>
                )}
                {availableDoses.map((dose) => {
                    // Key selection on the domain id, not the SOP UID:
                    // the id is guaranteed unique within the session.
                    const isSelected = currentDose?.id === dose.id;
                    const { item } = dose;
                    return (
                        <Box
                            key={dose.id}
                            borderTop="1px solid"
                            borderColor="divider"
                        >
                            <SelectableListItem
                                isSelected={isSelected}
                                onClick={
                                    item ? () => pickDose(item) : undefined
                                }
                                disabled={!dose.isLoaded}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                {dose.item ? (
                                    <DoseItem
                                        dose={dose.item}
                                        label={doseNameOf(dose)}
                                        onDelete={() =>
                                            setDeleteTarget(dose.item!)
                                        }
                                    />
                                ) : (
                                    <Typography
                                        fontSize={12}
                                        color="text.secondary"
                                    >
                                        {dose.label ?? 'Dose'}
                                    </Typography>
                                )}
                            </SelectableListItem>
                        </Box>
                    );
                })}
            </Box>

            <ConfirmationDialog
                open={deleteTarget !== null}
                title={intl.formatMessage({ id: 'deleteDose' })}
                message={intl.formatMessage({ id: 'deleteDoseConfirm' })}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
                mode="error"
            />
        </>
    );
}
